import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
} from '@etherisc/ui-kit';
import { ChevronLeftIcon, ScrollTextIcon } from 'lucide-react';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued';
import { apiClient } from '../../services/api';

interface AuditLogEntry {
  id: string;
  action: string;
  resource: string;
  resourceId: string | null;
  oldValues: Record<string, unknown> | null;
  newValues: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  userId: string | null;
  organizationId: string | null;
  correlationId: string;
  user: { id: string; name: string; email: string } | null;
  organization: { id: string; name: string } | null;
}

const ACTION_COLORS: Record<string, string> = {
  CREATE: 'bg-green-100 text-green-800',
  UPDATE: 'bg-blue-100 text-blue-800',
  DELETE: 'bg-red-100 text-red-800',
  LOGIN: 'bg-purple-100 text-purple-800',
  AUTOMATION_RUN: 'bg-orange-100 text-orange-800',
};

/** Unwrap backend envelope if present: { data, schemaId, schemaVersion } -> data */
function unwrapEnvelope(val: unknown): unknown {
  if (val && typeof val === 'object' && 'data' in val && 'schemaId' in val) {
    return (val as Record<string, unknown>).data;
  }
  return val;
}

function formatJson(data: unknown): string {
  return JSON.stringify(data, null, 2) ?? '';
}

const DIFF_STYLES = {
  variables: {
    light: {
      diffViewerBackground: '#f9fafb',
      addedBackground: '#dcfce7',
      addedColor: '#166534',
      removedBackground: '#fee2e2',
      removedColor: '#991b1b',
      wordAddedBackground: '#bbf7d0',
      wordRemovedBackground: '#fecaca',
      addedGutterBackground: '#bbf7d0',
      removedGutterBackground: '#fecaca',
      gutterBackground: '#f3f4f6',
      gutterBackgroundDark: '#e5e7eb',
      codeFoldBackground: '#f3f4f6',
      codeFoldGutterBackground: '#e5e7eb',
    },
  },
  line: { padding: '2px 10px', fontSize: '12px', wordBreak: 'break-word' as const },
  gutter: { padding: '2px 10px', minWidth: '30px', fontSize: '11px' },
  contentText: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', lineHeight: '1.5' },
};

function extractDiffSides(raw: unknown): { oldStr: string; newStr: string } {
  const data = unwrapEnvelope(raw);
  if (!data || typeof data !== 'object') return { oldStr: '', newStr: formatJson(data) };

  const rec = data as Record<string, unknown>;

  // UPDATE: backend stores { oldValues: {...}, newValues: {...} }
  if ('oldValues' in rec && 'newValues' in rec) {
    return { oldStr: formatJson(rec.oldValues), newStr: formatJson(rec.newValues) };
  }
  // Alternative naming: { old: {...}, new: {...} }
  if ('old' in rec && 'new' in rec) {
    return { oldStr: formatJson(rec.old), newStr: formatJson(rec.new) };
  }
  // DELETE: backend stores { deletedValues: {...} }
  if ('deletedValues' in rec) {
    return { oldStr: formatJson(rec.deletedValues), newStr: '' };
  }

  return { oldStr: '', newStr: formatJson(data) };
}

function DiffView({ oldValues, newValues }: { oldValues: Record<string, unknown> | null; newValues: Record<string, unknown> | null }) {
  if (!oldValues && !newValues) return <span className="text-gray-400">No data</span>;

  const fromOld = oldValues ? extractDiffSides(oldValues) : { oldStr: '', newStr: '' };
  const fromNew = newValues ? extractDiffSides(newValues) : { oldStr: '', newStr: '' };

  const oldStr = fromOld.oldStr || fromNew.oldStr;
  const newStr = fromOld.newStr || fromNew.newStr;

  if (!oldStr && !newStr) return <span className="text-gray-400">No data</span>;

  return (
    <div className="overflow-auto max-h-[700px] rounded-lg border border-gray-200">
      <ReactDiffViewer
        oldValue={oldStr}
        newValue={newStr}
        splitView={false}
        compareMethod={DiffMethod.WORDS}
        leftTitle={oldStr ? 'Before' : undefined}
        rightTitle={newStr ? 'After' : undefined}
        styles={DIFF_STYLES}
        useDarkTheme={false}
      />
    </div>
  );
}

export function AuditDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [entry, setEntry] = useState<AuditLogEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    apiClient.getAuditLogById(id)
      .then(data => setEntry(data as AuditLogEntry))
      .catch(() => setError('Failed to load audit entry'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  if (error || !entry) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl text-center py-16">
        <p className="text-red-600 mb-4">{error || 'Entry not found'}</p>
        <Button onClick={() => navigate('/admin/audit')}>Back to Audit Trail</Button>
      </div>
    );
  }

  const actionColor = ACTION_COLORS[entry.action] || 'bg-gray-100 text-gray-800';

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="outline"
          onClick={() => navigate('/admin/audit')}
          className="flex items-center gap-2"
        >
          <ChevronLeftIcon size={16} /> Back
        </Button>
        <ScrollTextIcon size={24} className="text-gray-600" />
        <div>
          <h1 className="text-xl font-bold text-gray-900">Audit Log Entry</h1>
          <span className="text-xs text-gray-500 font-mono">{entry.id}</span>
        </div>
      </div>

      <div className="space-y-6">
        {/* Overview */}
        <Card>
          <CardHeader>
            <CardTitle>Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <div>
                <span className="text-gray-500">Action</span>
                <div className="mt-0.5"><Badge className={actionColor}>{entry.action}</Badge></div>
              </div>
              <div>
                <span className="text-gray-500">Timestamp</span>
                <div className="mt-0.5 font-medium">{new Date(entry.createdAt).toLocaleString()}</div>
              </div>
              <div>
                <span className="text-gray-500">Resource</span>
                <div className="mt-0.5 font-medium">{entry.resource}</div>
              </div>
              <div>
                <span className="text-gray-500">Resource ID</span>
                <div className="mt-0.5 font-mono text-xs">{entry.resourceId || '—'}</div>
              </div>
              <div>
                <span className="text-gray-500">User</span>
                <div className="mt-0.5">
                  {entry.user ? (
                    <span>{entry.user.name} <span className="text-gray-400">({entry.user.email})</span></span>
                  ) : (
                    <span className="text-gray-400">{entry.userId || 'System'}</span>
                  )}
                </div>
              </div>
              <div>
                <span className="text-gray-500">Organization</span>
                <div className="mt-0.5">{entry.organization?.name || '—'}</div>
              </div>
              <div>
                <span className="text-gray-500">Correlation ID</span>
                <div className="mt-0.5 font-mono text-xs">{entry.correlationId}</div>
              </div>
              <div>
                <span className="text-gray-500">IP Address</span>
                <div className="mt-0.5 font-mono text-xs">{entry.ipAddress || '—'}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Changes / Details */}
        <Card>
          <CardHeader>
            <CardTitle>
              {entry.action === 'UPDATE' ? 'Changes' : entry.action === 'CREATE' ? 'Created Values' : 'Details'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <DiffView oldValues={entry.oldValues} newValues={entry.newValues} />
          </CardContent>
        </Card>

        {/* Raw Data */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Raw Data</CardTitle>
          </CardHeader>
          <CardContent>
            <details className="group">
              <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-900 select-none">
                Click to view raw JSON
              </summary>
              <pre className="mt-3 p-4 bg-gray-50 rounded-lg text-xs overflow-auto max-h-96">
                {JSON.stringify(entry, null, 2)}
              </pre>
            </details>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
